/**
 * PURPOSE: Estado del módulo de Resultados de Atletismo para un evento + deporte.
 * RESPONSIBILITIES: Una consulta compartida por `idevent:idsport`, renovada al minuto, y un hook con
 *   { loading, enabled, reason, canWrite, canOfficialize, canReopen, publicEnabled }.
 *
 *   FAIL CLOSED. Mientras carga, o si el servicio falló, todo es `false`: la pestaña "Resultados" no
 *   aparece hasta que el backend la confirme. Los permisos son solo pistas de interfaz; el servidor
 *   vuelve a validar rol, compañía y evento en cada petición.
 * COLLABORATORS: services/athleticsResultsService.js, sections/CategoriaSection.jsx. Mismo patrón que
 *   useProgrammingCenterStatus.js, con la caché separada por evento y deporte.
 * ROUTE: winscore/src/hooks/useAthleticsResultsStatus.js
 */
import { useEffect, useState } from "react";

import {
  ATHLETICS_RESULTS_STATUS_OFF,
  getAthleticsResultsStatus,
} from "../services/athleticsResultsService.js";

const TTL_MS = 60 * 1000;
const cache = new Map();

const scopeKey = (idevent, idsport) => `${Number(idevent)}:${Number(idsport)}`;

const isValidId = (value) => Number.isInteger(Number(value)) && Number(value) > 0;

export const loadAthleticsResultsStatus = ({ idevent, idsport }) => {
  const key = scopeKey(idevent, idsport);
  const cached = cache.get(key);
  if (!cached || Date.now() - cached.at > TTL_MS) {
    const entry = { at: Date.now(), promise: getAthleticsResultsStatus({ idevent, idsport }) };
    cache.set(key, entry);
    return entry.promise;
  }
  return cached.promise;
};

/** Para pruebas y para forzar una relectura (p. ej. tras cambiar de sesión). */
export const resetAthleticsResultsStatusCache = () => {
  cache.clear();
};

export const useAthleticsResultsStatus = ({ idevent, idsport, enabled = true }) => {
  const key = scopeKey(idevent, idsport);
  const active = Boolean(enabled) && isValidId(idevent) && isValidId(idsport);
  const [state, setState] = useState({ key: null, status: ATHLETICS_RESULTS_STATUS_OFF });

  useEffect(() => {
    if (!active) return undefined;
    let mounted = true;
    const [eventId, sportId] = key.split(":").map(Number);
    loadAthleticsResultsStatus({ idevent: eventId, idsport: sportId }).then((status) => {
      if (mounted) setState({ key, status });
    });
    return () => {
      mounted = false;
    };
  }, [key, active]);

  if (!active) return { loading: false, ...ATHLETICS_RESULTS_STATUS_OFF };
  if (state.key !== key) return { loading: true, ...ATHLETICS_RESULTS_STATUS_OFF };
  return { loading: false, ...state.status };
};
